import React from 'react';

import { defineMessages } from 'react-intl';
import { Context } from '@alltrails/shared/types/context';
import useIsMounted from '@alltrails/shared/hooks/useIsMounted';
import User from '@alltrails/shared/types/User';
import useFormatMessage from 'hooks/useFormatMessage';

import * as styles from './styles/styles.module.scss';
import HomeSearchBar from '../../search/HomeSearchBar';
import RotatingHero from '../../shared/RotatingHero';
import { betweenHours } from '../../../utils/time_helpers';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { Greetings } from '../../../pages/HomePage/HomePage';

const messages = defineMessages({
  EXPLORE_NEARYBY_TRAILS: {
    defaultMessage: 'Explore nearby trails'
  },
  FIND_YOUR_NEXT_TRAIL: {
    defaultMessage: 'Find your next trail'
  }
});

const FIRST_NAME_PLACEHOLDER = '{first_name}';

const chooseGreeting = (greetings: Greetings, currentUser: User, greetingIndex: number, defaultText: string): string => {
  if (!(currentUser && greetings)) {
    return defaultText;
  }

  let text = '';
  greetings.forEach(greet => {
    if (text.length === 0) {
      if (greet.daily_start_time && greet.daily_end_time && betweenHours(greet.daily_start_time, greet.daily_end_time)) {
        text = greet.custom_title[greetingIndex];
      }
    }
  });

  if (currentUser && currentUser.firstName && text.includes('{first_name}')) {
    text = text.replace(FIRST_NAME_PLACEHOLDER, currentUser.firstName);
  }

  return text;
};

type Props = {
  context: Context;
  greetings?: Greetings;
  heroData: {
    greetingIndex: number;
    images: {
      alt: string;
      w2800Jpg: string;
    }[];
  };
  latLng: (string | number)[];
  windowWidth: number;
};

const HomeHeader = ({ windowWidth, latLng, context, greetings, heroData }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: { EXPLORE_NEARYBY_TRAILS, FIND_YOUR_NEXT_TRAIL }
  } = useFormatMessage(messages);

  const isMounted = useIsMounted();

  const isCurrentUser = context.currentUser !== null;

  const deviceType = context.mobileBrowser || (windowWidth && windowWidth <= DESKTOP_BREAKPOINT) ? 'mobile' : 'desktop';

  const headerText = chooseGreeting(greetings, context.currentUser, heroData.greetingIndex, FIND_YOUR_NEXT_TRAIL);

  if (isCurrentUser) {
    const buttonContainerStyle = deviceType === 'mobile' ? { marginBottom: 36 } : null;

    return (
      <RotatingHero duration={6000} images={heroData.images} className={styles.loggedInContainer} buttonContainerStyle={buttonContainerStyle}>
        <h1 className={`${styles.header} xlate-none`}>{isMounted() ? headerText : ''}</h1>
        <div className={`${styles.searchBarContainer} ${styles.loggedInSearchContainer}`}>
          <HomeSearchBar windowWidth={windowWidth} latLng={latLng} context={context} />
        </div>
      </RotatingHero>
    );
  }

  return (
    <RotatingHero duration={6000} images={heroData.images} className={styles.container}>
      <div className={styles.contentContainer}>
        <h1 className={`${styles.header} xlate-none`}>{headerText}</h1>
        <div className={styles.searchBarContainer}>
          <HomeSearchBar windowWidth={windowWidth} latLng={latLng} context={context} />
        </div>
        <div className={styles.exploreLink}>
          <a href={LanguageSupportUtil.wrapUrlSafe('/explore?ref=homepage_explore_homeheader', context.languageRegionCode)}>
            {EXPLORE_NEARYBY_TRAILS}
          </a>
        </div>
      </div>
    </RotatingHero>
  );
};

export default HomeHeader;
